// 3rd Party
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
// Components
import { CloseOutlined } from 'opencare-crown';

// Other
import {
  updateCheckoutErrorStatus,
  updateCheckoutFormData,
  updatePatient,
  updateSelectedAppointment,
  updateLoadingLogin,
} from 'connect/actions';
import {
  makeSelectCheckoutData,
  selectDataClinic,
  selectDataRequestedAppointment,
  selectDataPatient,
  selectDataLoginFormError,
} from 'connect/selectors';
import { UpdatePatientSilently } from 'connect/saga/handlers/update-patient-silently/update-patient-silently';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { usePartnerStorage } from 'pages/partner/storage/Storage';

import { UserSourceStateHandler } from 'connect/state/handlers/user';

import { UTM_SOURCES } from 'common/constants';

import { useOfferService } from 'services/OfferService';

import * as Url from '../../../../common/helpers/url-helpers';
import { selectDataCheckoutFormErrorStatus } from '../../../connect/selectors';

import {
  getCheckoutStepId,
  getCheckoutSteps,
  nextActiveStepName,
} from '../../../helpers/checkout-modal-helpers';

import { CheckoutTimer } from '../CheckoutTimer/CheckoutTimer';
import ProgressBar from '../ProgressBar/ProgressBar';
import OfferBannerCheckout from '../OfferBannerCheckout/OfferBannerCheckout';

import { MobileCloseButton, StyledModal } from './styled-components';

// Component
const CheckoutModalStepHandler = ({
  history,
  location,
  routeMatch,
  isPartner,
}) => {
  const dispatch = useDispatch();
  const { clinicId } = routeMatch.params;

  const offerService = useOfferService();

  const checkoutData = useSelector(makeSelectCheckoutData);
  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );
  const stepId = getCheckoutStepId(
    routeMatch.params.checkoutStep,
    checkoutData,
  );
  const step = getCheckoutSteps(checkoutData)[stepId];

  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const clinic = useSelector(selectDataClinic);
  const patient = useSelector(selectDataPatient);
  const loginError = useSelector(selectDataLoginFormError);
  const userSource = useSelector(UserSourceStateHandler.selector);

  const partnerStorage = usePartnerStorage();
  const funnelStorage = useFunnelStorage();
  const storage = isPartner ? partnerStorage : funnelStorage;

  const currentTimeISO = moment.utc().toISOString();

  // If the patient is logged in, set the startedCheckoutAt time if it's not set already
  // Since this component is rendered as long as the modal is open, and listens to `patient`,
  // as soon as the patient is set, we will set the startedCheckoutAt time
  useEffect(() => {
    if (!patient?.id || patient?.startedCheckoutAt) {
      return;
    }

    dispatch(
      UpdatePatientSilently.createAction({
        patientId: patient.id,
        startedCheckoutAt: currentTimeISO,
        lastSeenAt: currentTimeISO,
      }),
    );
  }, [patient]);

  useEffect(() => {
    // If the user is coming from a magic link to the checkout, handle this differently
    if (userSource === UTM_SOURCES.MEMBER_ID_REMINDER) {
      // Close if we failed to log the user in
      if (loginError || clinic.slots?.includes(requestedAppointment.slotId)) {
        // Handle this gracefully
        history.push(`/results/${clinicId}/checkout/return-error`);
        return;
      }
      // Wait for the data to load
      if (!requestedAppointment.providerId) {
        return;
      }
    }

    // Close checkout modal when:
    // 1. if someone hasn't got a requested appointment (may happen when they navigate directly to a checkout step)
    // 2. if someone visits a URL that doesn't exist
    if (!requestedAppointment.providerId || !step) {
      handleClose();
    }
    // Clear previous checkout form error status when user lands on checkout modal
    if (checkoutFormErrorStatus) {
      dispatch(updateCheckoutErrorStatus(null));
    }
  }, [requestedAppointment]);

  useEffect(() => {
    // Wait for clinic to load
    if (!clinic.id) {
      return;
    }

    // If provider object not in store, fetch it
    if (requestedAppointment.providerId && !requestedAppointment.provider) {
      const foundProvider = clinic.providers?.find(
        p => p.id === requestedAppointment.providerId,
      );

      if (!foundProvider) {
        handleClose();
        return;
      }

      dispatch(
        updateSelectedAppointment(
          requestedAppointment.providerId,
          requestedAppointment.slotId,
          foundProvider,
          requestedAppointment.anyProviderSelected,
        ),
      );
    }
  }, [clinic, requestedAppointment]);

  if (step && !step.validate(checkoutData.formData)) {
    Url.replaceEnd(history, location, 'patient');
  }

  const handleClose = () => {
    dispatch(updateLoadingLogin(null));
    dispatch(updateCheckoutErrorStatus(null));
    if (isPartner) {
      dispatch(updatePatient(null));
    }
    history.push(`/results/${clinicId}`);
  };

  const handleNextStep = formValues => {
    const nextStepName = nextActiveStepName(stepId, checkoutData);
    Url.pushEnd(history, location, nextStepName);
    dispatch(updateCheckoutFormData(formValues));
  };

  if (!step) {
    return null;
  }
  if (step.isHidden(checkoutData)) {
    handleNextStep();
  }

  return (
    <StyledModal
      centered
      footer={null}
      onCancel={handleClose}
      visible
      width={step.modalWidth(checkoutData)}
      maskClosable
    >
      <MobileCloseButton type="button" onClick={handleClose}>
        <CloseOutlined />
      </MobileCloseButton>
      {offerService.hasActiveOffer 
        && (
          <OfferBannerCheckout
            offerName={offerService.offerName}
            isThirdPartyOffer={offerService.isThirdPartyOffer}
          />
        )
      }
      <CheckoutTimer handleClose={handleClose} />
      <ProgressBar data={checkoutData} stepId={stepId + 1} />
      {!step.isHidden(checkoutData)
        && step.render(
          handleNextStep,
          step.state(checkoutData, storage),
          handleClose,
        )}
    </StyledModal>
  );
};

CheckoutModalStepHandler.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  routeMatch: PropTypes.object.isRequired,
  isPartner: PropTypes.bool.isRequired,
};

export default CheckoutModalStepHandler;
